import React from 'react';
import { ArrowRight } from '../../components/Glyph';
import { Card, Kind, Greeting, FourBar, Need, NeedsCard, HomeFrame, today, dueState, DueChip } from './parts';
import ReleaseNote from '../../components/ReleaseNote';

/*
  Rep Home.

  Built 5 September 2026 against the approved drawing in docs/design/group-c/
  (signed off 31 August 2026). Same three blocks and the same furniture as the
  other two Homes, asking the rep's question: what do I owe.

    1  Still to count             how many locations and how many blank lines
    2  Carry on where you left off  the sheet already open, and the way back in
    3  Needs you                  tasks, unread notifications, and what has not
                                  been started

  **Block two is the one that earns its room.** A rep counts a location over
  several visits, and the thing they want on opening the app is almost always the
  sheet they were last on. Everything else on this screen is a summary; that
  block is a door.

  **The headline counts BLANK LINES, not lines.** Rule 9: a blank is not a zero.
  A blank means nobody has been to that shelf and a zero means somebody went and
  found none, and the number a rep needs is how many shelves are still unvisited.
*/

export default function RepHome({
  profile, cycle, stats, total, pct, owed, linesLeft,
  carryOn, taskCount = 0, unreadCount = 0,
  onContinue, onOpenTasks, onOpenMyCounts,
}) {
  /*
    **The lines half of the sentence is dropped when there are none**, and that
    was found by looking. "You have 17 locations left and 0 lines still to
    count" is two true halves that contradict each other to a person reading
    them, because a location that has not been started yet has no lines on it to
    be blank. When there are none, the locations are the whole story.
  */
  const due = dueState(cycle?.due_on);
  const tail = due ? ' ' + due.line : '';
  const line = !cycle
    ? `${today()}. No count cycle is open.`
    : linesLeft > 0
      ? `${today()}. You have ${owed} location${owed === 1 ? '' : 's'} left and ${linesLeft} line${linesLeft === 1 ? '' : 's'} still to count.${tail}`
      : `${today()}. You have ${owed} location${owed === 1 ? '' : 's'} left to count.${tail}`;

  const headline = cycle ? (
    <Card>
      <div style={{ display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap' }}>
        <Kind>Still to count</Kind>
        <DueChip due={due} />
      </div>
      <div style={{ display: 'flex', alignItems: 'flex-end', gap: 16, flexWrap: 'wrap', margin: '8px 0 14px' }}>
        <div>
          <span style={{ fontSize: 34, fontWeight: 800, color: 'var(--text-strong)', letterSpacing: '-0.6px' }}>{owed}</span>
          <span style={{ marginLeft: 8, fontSize: 13, fontWeight: 600, color: 'var(--text-secondary)' }}>
            location{owed === 1 ? '' : 's'}
            {linesLeft > 0 && <><br />{linesLeft} line{linesLeft === 1 ? '' : 's'} still blank</>}
          </span>
        </div>
        <span style={{ flex: 1 }} />
        <div style={{ textAlign: 'right' }}>
          <Kind>Done</Kind>
          <div style={{ fontSize: 34, fontWeight: 800, color: 'var(--action-text)', lineHeight: 1.05 }}>{pct}%</div>
        </div>
      </div>
      <FourBar notStarted={stats.not_started} inProgress={stats.in_progress}
        submitted={stats.submitted} approved={stats.approved} />
      <div style={{ fontSize: 12.5, color: 'var(--text-secondary)', marginTop: 14, lineHeight: 1.55 }}>
        {total} location{total === 1 ? '' : 's'} in {cycle.name}.
      </div>
    </Card>
  ) : (
    <Card pad="32px 24px" style={{ textAlign: 'center' }}>
      <div className="oh-title" style={{ fontSize: 17, fontWeight: 800, color: 'var(--text-primary)' }}>No count cycle is open</div>
      <div style={{ fontSize: 13, color: 'var(--text-secondary)', marginTop: 8, lineHeight: 1.6, maxWidth: 360, marginLeft: 'auto', marginRight: 'auto' }}>
        There is nothing to count yet. An administrator opens a cycle and your
        locations appear here.
      </div>
    </Card>
  );

  // **Only ever shown when there IS one.** A "carry on" block with nothing to
  // carry on from is a door onto nothing, and a rep who has finished everything
  // should see that they have, not an empty promise.
  const carry = carryOn ? (
    <Card>
      <Kind>Carry on where you left off</Kind>
      <div style={{ display: 'flex', alignItems: 'stretch', gap: 14, marginTop: 12, flexWrap: 'wrap' }}>
        <div style={{ width: 4, background: 'var(--accent)', borderRadius: 2, alignSelf: 'stretch', minHeight: 44 }} />
        <div style={{ flex: 1, minWidth: 180 }}>
          <div style={{ fontSize: 9.5, fontWeight: 800, letterSpacing: 1, color: 'var(--text-secondary)' }}>{carryOn.terr}</div>
          <div className="oh-title" style={{ fontSize: 15, fontWeight: 700, color: 'var(--text-primary)', lineHeight: 1.25 }}>{carryOn.name}</div>
          <div style={{ display: 'flex', alignItems: 'center', gap: 9, marginTop: 7 }}>
            <span style={{ fontSize: 12, fontWeight: 800, color: 'var(--text-primary)' }}>
              {carryOn.done}<span style={{ color: 'var(--text-secondary)', fontWeight: 600 }}> / {carryOn.total} lines</span>
            </span>
            <span style={{ flex: 1, maxWidth: 220 }}>
              <span style={{ display: 'block', height: 4, borderRadius: 2, background: 'var(--border-soft)', overflow: 'hidden' }}>
                <span style={{ display: 'block', height: '100%', width: (carryOn.total ? Math.round(carryOn.done / carryOn.total * 100) : 0) + '%', background: 'var(--accent)' }} />
              </span>
            </span>
          </div>
        </div>
        <button className="btn btn-primary" style={{ height: 44, alignSelf: 'center' }}
          onClick={() => onContinue(carryOn.id)}>Continue count</button>
      </div>
    </Card>
  ) : (
    <Card>
      <Kind>Carry on where you left off</Kind>
      <div style={{ fontSize: 13, color: 'var(--text-secondary)', marginTop: 10, lineHeight: 1.6 }}>
        Nothing is part way through. Every sheet you have started is finished.
      </div>
    </Card>
  );

  const needs = (
    <NeedsCard title="Needs you" footer={<>Open Tasks <ArrowRight size={13} /></>} onFooter={onOpenTasks}
      rows={
        <>
          <Need n={taskCount} title="Tasks" sub="Things you have raised or been given"
            tone="var(--accent-ink)" onClick={onOpenTasks} />
          <Need n={unreadCount} title="Unread notifications" sub="What happened to your counts"
            onClick={onOpenTasks} />
          <Need n={stats.not_started} title="Not started" sub="Locations you have not been to yet"
            tone="var(--critical-ink)" onClick={onOpenMyCounts} />
        </>
      } />
  );

  return (
    <div>
      {/* **What changed in OnHand, above the greeting.** The owner's own
          instruction of 5 September 2026: the release note stops taking the
          whole screen and becomes a notice here with one button that clears
          it. It draws nothing at all when there is no unread note, which is
          almost always. Group H's drawing, and it changes this approved
          Group C screen because he asked for it. */}
      <ReleaseNote />
      <Greeting name={profile?.full_name} line={line} />
      <HomeFrame headline={headline} needs={needs} third={carry} />
    </div>
  );
}
